import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import { useLang } from '../hooks/useLang';
import './LoggedInHome.css';

export default function LoggedInHome() {
  const { user, profile } = useAuth();
  const { t } = useLang();
  const [upcomingTrips, setUpcomingTrips] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [myTrips, setMyTrips] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const isSender = profile?.role === 'sender' || profile?.role === 'both';
  const isTransporter = profile?.role === 'transporter' || profile?.role === 'both';

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  async function fetchData() {
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];

    const [tripsRes, bookingsRes, myTripsRes] = await Promise.all([
      supabase.from('trips')
        .select('*, profiles!transporter_id(full_name, rating, is_verified)')
        .eq('status', 'active')
        .gte('departure_date', today)
        .order('departure_date', { ascending: true })
        .limit(3),
      supabase.from('bookings')
        .select('*, trips(origin_city, destination_city, departure_date)')
        .eq('sender_id', user.id)
        .not('status', 'eq', 'cancelled')
        .order('created_at', { ascending: false })
        .limit(3),
      supabase.from('trips')
        .select('*')
        .eq('transporter_id', user.id)
        .eq('status', 'active')
        .order('departure_date', { ascending: true })
        .limit(3),
    ]);

    const freshBookings = bookingsRes.data || [];
    const freshTrips = myTripsRes.data || [];

    setUpcomingTrips(tripsRes.data || []);
    setMyBookings(freshBookings);
    setMyTrips(freshTrips);

    let transporterBookingIds = [];
    if (freshTrips.length > 0) {
      const tripIds = freshTrips.map(t => t.id);
      const { data: tb } = await supabase
        .from('bookings').select('id').in('trip_id', tripIds);
      transporterBookingIds = (tb || []).map(b => b.id);
    }

    const allBookingIds = [
      ...freshBookings.map(b => b.id),
      ...transporterBookingIds,
    ];

    if (allBookingIds.length > 0) {
      const { data: unread } = await supabase
        .from('messages').select('id')
        .in('booking_id', allBookingIds)
        .eq('read', false)
        .neq('sender_id', user.id);
      setUnreadCount(unread?.length || 0);
    }

    setLoading(false);
  }

  const firstName = profile?.full_name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const STATUS_COLOR = {
    pending: 'badge-yellow', confirmed: 'badge-green', collected: 'badge-green',
    in_transit: 'badge-yellow', delivered: 'badge-green', cancelled: 'badge-gray',
  };

  return (
    <div className="page lih-page">
      <div className="container">

        {/* GREETING */}
        <div className="lih-greeting">
          <div>
            <h1>{greeting}, {firstName} 👋</h1>
            <p>{t('loggedInHome.subtitle')}</p>
          </div>
          <div className="lih-quick-actions">
            {isSender && <Link to="/search" className="btn btn-primary">{t('loggedInHome.findTransporter')}</Link>}
            {isTransporter && <Link to="/post-trip" className="btn btn-outline">+ Post a Trip</Link>}
          </div>
        </div>

        {/* STAT CARDS */}
        <div className="lih-stats">
          {isSender && (
            <Link to="/dashboard" className="lih-stat-card lih-stat-link">
              <div className="lih-stat-icon">📦</div>
              <div className="lih-stat-val">{myBookings.length}</div>
              <div className="lih-stat-label">{t('loggedInHome.activeBookings')}</div>
              <div className="lih-stat-arrow">→</div>
            </Link>
          )}

          {isTransporter && (
            <Link to="/dashboard?tab=trips" className="lih-stat-card lih-stat-link">
              <div className="lih-stat-icon">✈️</div>
              <div className="lih-stat-val">{myTrips.length}</div>
              <div className="lih-stat-label">{t('loggedInHome.activeTrips')}</div>
              <div className="lih-stat-arrow">→</div>
            </Link>
          )}

          {isTransporter && (
            <Link to="/dashboard?tab=requests" className="lih-stat-card lih-stat-link">
              <div className="lih-stat-icon">📬</div>
              <div className="lih-stat-val">
                {myTrips.reduce((acc, t) => acc, 0)}
              </div>
              <div className="lih-stat-label">Booking requests</div>
              <div className="lih-stat-arrow">→</div>
            </Link>
          )}

          <Link
            to="/dashboard?tab=messages"
            className={'lih-stat-card lih-stat-link' + (unreadCount > 0 ? ' lih-stat-card-alert' : '')}
          >
            <div className="lih-stat-icon">💬</div>
            <div className="lih-stat-val">{unreadCount}</div>
            <div className="lih-stat-label">Unread messages</div>
            <div className="lih-stat-arrow" style={{ color: unreadCount > 0 ? 'var(--red)' : '' }}>→</div>
          </Link>

          <Link to="/profile" className="lih-stat-card lih-stat-link">
            <div className="lih-stat-icon">⭐</div>
            <div className="lih-stat-val">{profile?.rating?.toFixed(1) || '—'}</div>
            <div className="lih-stat-label">Your rating</div>
            <div className="lih-stat-arrow">→</div>
          </Link>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="spinner" />
          </div>
        ) : (
          <div className="lih-grid">

            {/* MY ACTIVE BOOKINGS — senders only */}
            {isSender && (
              <div className="lih-section">
                <div className="lih-section-header">
                  <h2>{t('loggedInHome.myBookings')}</h2>
                  <Link to="/dashboard" className="lih-see-all">See all →</Link>
                </div>
                {myBookings.length === 0 ? (
                  <div className="lih-empty">
                    <p>{t('loggedInHome.noBookings')}</p>
                    <Link to="/search" className="btn btn-primary" style={{ marginTop: '12px' }}>Browse trips</Link>
                  </div>
                ) : (
                  <div className="lih-list">
                    {myBookings.map(b => (
                      <div className="lih-item" key={b.id}>
                        <div className="lih-item-icon">📦</div>
                        <div className="lih-item-info">
                          <div className="lih-item-title">{b.trips?.origin_city} → {b.trips?.destination_city}</div>
                          <div className="lih-item-sub">
                            {b.trips?.departure_date && format(new Date(b.trips.departure_date), 'MMM d, yyyy')} · {b.kg_booked} kg
                          </div>
                        </div>
                        <span className={'badge ' + STATUS_COLOR[b.status]}>{b.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* MY TRIPS — transporters only */}
            {isTransporter && (
              <div className="lih-section">
                <div className="lih-section-header">
                  <h2>{t('loggedInHome.myTrips')}</h2>
                  <Link to="/dashboard?tab=trips" className="lih-see-all">See all →</Link>
                </div>
                {myTrips.length === 0 ? (
                  <div className="lih-empty">
                    <p>{t('loggedInHome.noTrips')}</p>
                    <Link to="/post-trip" className="btn btn-primary" style={{ marginTop: '12px' }}>Post a trip</Link>
                  </div>
                ) : (
                  <div className="lih-list">
                    {myTrips.map(t => {
                      const pct = Math.round(((t.total_kg - t.available_kg) / t.total_kg) * 100);
                      return (
                        <div className="lih-item" key={t.id}>
                          <div className="lih-item-icon">✈️</div>
                          <div className="lih-item-info">
                            <div className="lih-item-title">{t.origin_city} → {t.destination_city}</div>
                            <div className="lih-item-sub">{format(new Date(t.departure_date), 'MMM d')} · {t.available_kg} kg left</div>
                            <div className="capacity-bar" style={{ marginTop: '6px', height: '3px' }}>
                              <div className="capacity-fill" style={{ width: pct + '%' }} />
                            </div>
                          </div>
                          <span className="badge badge-green">{pct}% booked</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* UPCOMING TRIPS TO DISCOVER — senders only */}
            {isSender && (
              <div className="lih-section lih-section-wide">
                <div className="lih-section-header">
                  <h2>{t('loggedInHome.upcomingTrips')}</h2>
                  <Link to="/search" className="lih-see-all">See all →</Link>
                </div>
                {upcomingTrips.length === 0 ? (
                  <div className="lih-empty"><p>No upcoming trips at the moment.</p></div>
                ) : (
                  <div className="lih-trips-row">
                    {upcomingTrips.map(t => (
                      <Link to={'/trip/' + t.id} key={t.id} className="lih-trip-card">
                        <div className="lih-trip-top">
                          <div className="lih-trip-avatar">{t.profiles?.full_name?.[0] || '?'}</div>
                          <div>
                            <div className="lih-trip-name">{t.profiles?.full_name}</div>
                            <div className="lih-trip-date">{format(new Date(t.departure_date), 'MMM d, yyyy')}</div>
                          </div>
                          {t.profiles?.is_verified && <span className="badge badge-green" style={{ marginLeft: 'auto' }}>✓</span>}
                        </div>
                        <div className="lih-trip-route">{t.origin_city} → {t.destination_city}</div>
                        <div className="lih-trip-footer">
                          <span className="lih-trip-price">€{t.price_per_kg}/kg</span>
                          <span className="lih-trip-kg">{t.available_kg} kg left</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}